import { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { HELP_BOT_NUDGE_PROMPTS } from '../helpBotPrompts'

const STORAGE_KEYS = {
    NUDGES_DISMISSED_FOREVER: 'helpBot_nudgesDismissedForever',
}

const NUDGE_DELAY = 3000 // 3 seconds after landing on page

/**
 * Get routes that are permanently dismissed
 */
const getDismissedRoutes = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEYS.NUDGES_DISMISSED_FOREVER)
        return stored ? new Set(JSON.parse(stored)) : new Set()
    } catch {
        return new Set()
    }
}

/**
 * Hook to manage contextual HelpBot nudges
 *
 * @param {Object} options
 * @param {boolean} options.isHelpBotOpen - Whether the HelpBot panel is open
 * @returns {Object} Nudge state and handlers
 */
export const useHelpBotNudge = ({ isHelpBotOpen }) => {
    const location = useLocation()
    const [isVisible, setIsVisible] = useState(false)
    // Use ref to track shown routes to avoid re-triggering effect
    const shownThisSessionRef = useRef(new Set())

    // Get the current route's base path (e.g., /canvas/123 -> /canvas)
    const currentBasePath = (() => {
        const path = location.pathname
        // Match against known routes, handling dynamic segments
        const knownRoutes = Object.keys(HELP_BOT_NUDGE_PROMPTS)
        return knownRoutes.find((route) => path === route || path.startsWith(`${route}/`)) || null
    })()

    // Get nudge config for current route
    const nudgeConfig = currentBasePath ? HELP_BOT_NUDGE_PROMPTS[currentBasePath] : null

    // Check if running on localhost
    const isLocalhost =
        window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

    // Determine if nudge should show
    useEffect(() => {
        // Don't show if:
        // - Running on localhost
        // - HelpBot is already open
        // - No nudge config for this route
        // - Already shown this session
        // - Permanently dismissed
        if (
            isLocalhost ||
            isHelpBotOpen ||
            !nudgeConfig ||
            shownThisSessionRef.current.has(currentBasePath) ||
            getDismissedRoutes().has(currentBasePath)
        ) {
            return
        }

        // Show nudge after delay
        const timer = setTimeout(() => {
            setIsVisible(true)
            shownThisSessionRef.current.add(currentBasePath)
        }, NUDGE_DELAY)

        return () => clearTimeout(timer)
    }, [currentBasePath, nudgeConfig, isHelpBotOpen, isLocalhost])

    // Hide when HelpBot opens
    useEffect(() => {
        if (isHelpBotOpen) {
            setIsVisible(false)
        }
    }, [isHelpBotOpen])

    // Dismiss handlers
    const dismiss = useCallback(() => {
        setIsVisible(false)
    }, [])

    const dismissForever = useCallback(() => {
        setIsVisible(false)
        if (currentBasePath) {
            const currentDismissed = getDismissedRoutes()
            const newDismissed = new Set([...currentDismissed, currentBasePath])
            localStorage.setItem(
                STORAGE_KEYS.NUDGES_DISMISSED_FOREVER,
                JSON.stringify([...newDismissed])
            )
        }
    }, [currentBasePath])

    return {
        isVisible,
        nudgeConfig,
        dismiss,
        dismissForever,
    }
}
